import type { ChatConfigPayload, NewMessagesPayload, PullAllMessagesRequest, PullAllMessagesResponse, PullMessagesRequest, PullMessagesResponse, SendMessagesRequest, SendMessagesResponse } from '@proj-airi/server-sdk-shared'
import type { ComputedRef, Ref } from 'vue'

import { defineInvoke } from '@moeru/eventa'
import { createContext as createWsContext, wsErrorEvent } from '@moeru/eventa/adapters/websocket/native'
import { chatConfig, newMessages, pullAllMessages, pullMessages, sendMessages } from '@proj-airi/server-sdk-shared'
import { useWebSocket } from '@vueuse/core'
import { computed, ref, shallowRef, watch } from 'vue'

import * as v from 'valibot'

const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30_000
const RECONNECT_RETRIES = -1

/**
 * Server-side auth rejection close code (IANA application range 4000-4999).
 *
 * Browsers swallow the HTTP 401 status when a WebSocket upgrade is rejected,
 * so the only way for the server to distinguish "wrong token, stop retrying"
 * from a transient network drop on the client is to accept the upgrade and
 * close with a custom application code. The server emits this from
 * `apps/server/src/app.ts` when `resolveRequestAuth` returns null.
 */
export const WS_CLOSE_UNAUTHORIZED = 4001

// NOTICE:
// The native ws adapter's context type is not directly exported from
// `@moeru/eventa/adapters/websocket/native`; use the inferred return type so
// `ctx.on` / `ctx.emit` overloads stay accurate.
// Source: @moeru/eventa@0.3.0 — adapter exports only `createContext` and the
// event constants.
// Removal condition: the adapter exports a public `EventContext` type.
type WsEventContext = ReturnType<typeof createWsContext>['context']

const NewMessagesPayloadSchema = v.object({
  chatId: v.pipe(v.string(), v.minLength(1)),
  fromSeq: v.number(),
  toSeq: v.number(),
  messages: v.array(v.object({
    id: v.pipe(v.string(), v.minLength(1)),
    chatId: v.pipe(v.string(), v.minLength(1)),
    senderId: v.nullable(v.string()),
    role: v.picklist(['system', 'user', 'assistant', 'tool', 'error']),
    content: v.string(),
    seq: v.number(),
    createdAt: v.number(),
    updatedAt: v.number(),
  })),
})

const ChatConfigPayloadSchema = v.object({
  pushDebounceMs: v.pipe(v.number(), v.minValue(0)),
})

/**
 * WebSocket connection lifecycle states surfaced to the chat-sync layer.
 *
 * - `idle`: never connected, or `disconnect()` was called and we are not
 *   trying to reconnect.
 * - `connecting`: WebSocket handshake in flight (initial or reconnect attempt).
 * - `open`: socket open and `wsConnectedEvent` fired.
 * - `closed`: lost the socket; auto-reconnect may bring it back to `connecting`.
 */
export type ChatWsStatus = 'idle' | 'connecting' | 'open' | 'closed'

/**
 * Disposer returned by `onNewMessages` / `onStatusChange`. Calling it removes
 * the listener; safe to call multiple times.
 */
export type ChatWsUnsubscribe = () => void

export interface CreateChatWsClientOptions {
  /**
   * Base server URL, e.g. `https://api.airi.build`. The client appends
   * `/ws/chat?token=<jwt>` to build the WebSocket URL.
   */
  serverUrl: string
  /**
   * Resolves the current bearer token at connect/reconnect time. Returning
   * `null` skips connecting (the user is not authenticated).
   */
  getToken: () => string | null
}

export interface ChatWsClient {
  /** Current connection status. Useful for UI banners. */
  status: () => ChatWsStatus
  /** Connect (or reconnect with the latest token). No-op if already open. */
  connect: () => void
  /** Close the socket and stop auto-reconnect until the next `connect()`. The handle is reusable. */
  disconnect: () => void
  /** Permanently dispose the client (stops the status watcher). After `destroy()` the handle is unusable. */
  destroy: () => void
  /** RPC: push messages to a chat. Rejects if disconnected mid-flight. */
  sendMessages: (req: SendMessagesRequest) => Promise<SendMessagesResponse>
  /** RPC: pull messages newer than `afterSeq`. Rejects if disconnected mid-flight. */
  pullMessages: (req: PullMessagesRequest) => Promise<PullMessagesResponse>
  /**
   * RPC: batch-pull messages newer than per-chat cursors. One round trip
   * replaces N parallel `pullMessages` calls during reconcile.
   */
  pullAllMessages: (req: PullAllMessagesRequest) => Promise<PullAllMessagesResponse>
  /**
   * Subscribe to inbound `newMessages` push. The handler fires for every
   * authenticated push, including potential echoes of the local sender — the
   * caller MUST dedup by message id.
   */
  onNewMessages: (handler: (payload: NewMessagesPayload) => void) => ChatWsUnsubscribe
  /**
   * Subscribe to server-pushed client config. Fires once right after the
   * handshake completes (and again on every reconnect) so the caller can
   * apply server-side tunables (push debounce window, etc.) without an
   * extra HTTP round trip.
   */
  onConfig: (handler: (payload: ChatConfigPayload) => void) => ChatWsUnsubscribe
  /** Subscribe to status transitions for UI / catchup orchestration. */
  onStatusChange: (handler: (status: ChatWsStatus) => void) => ChatWsUnsubscribe
}

/**
 * Build the `/ws/chat?token=<jwt>` URL from a base server URL.
 *
 * Before:
 * - "https://api.airi.build", token="abc"
 *
 * After:
 * - "wss://api.airi.build/ws/chat?token=abc"
 *
 * @internal
 */
export function buildChatWsUrl(serverUrl: string, token: string): string {
  // Use URL parsing instead of string concat so trailing slashes / paths in
  // serverUrl are normalized cleanly.
  const url = new URL(serverUrl)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.pathname = `${url.pathname.replace(/\/+$/, '')}/ws/chat`
  url.searchParams.set('token', token)
  return url.toString()
}

/**
 * Compute exponential reconnect delay with bounded jitter.
 *
 * Math context: VueUse's autoReconnect supplies `retries` starting at 1 for
 * the first reconnect. The minimum 50% floor keeps the immediate retry from
 * firing in <50ms (a 0..exp uniform jitter previously could fire at ~0ms,
 * producing reconnect storms across many tabs against a hard-down server).
 *
 * @internal
 */
export function computeReconnectDelay(retries: number, baseMs: number, maxMs: number): number {
  const exp = Math.min(maxMs, baseMs * 2 ** Math.max(0, retries - 1))
  // 50% floor + 50% jitter window; total range is [exp/2, exp).
  return Math.floor(exp * 0.5 + Math.random() * exp * 0.5)
}

/**
 * Map VueUse's 3-state status onto the chat-sync 4-state machine.
 *
 * VueUse exposes `OPEN | CONNECTING | CLOSED`. Chat-sync needs to distinguish
 * "never connected / explicitly disconnected" (`idle`) from "lost the socket
 * and auto-reconnect is pending" (`closed`). The caller tracks the user
 * intent via `enabled`; here we just translate the transport state.
 *
 * @internal
 */
export function mapStatus(vue: 'OPEN' | 'CONNECTING' | 'CLOSED', enabled: boolean): ChatWsStatus {
  if (vue === 'OPEN')
    return 'open'
  if (vue === 'CONNECTING')
    return 'connecting'
  return enabled ? 'closed' : 'idle'
}

/**
 * Create a chat-sync WebSocket client backed by VueUse's `useWebSocket` plus
 * eventa's native ws adapter for the eventa context that handles RPC and
 * outbound subscription routing.
 *
 * Use when:
 * - The user is signed in and the chat store wants real-time sync.
 *
 * Expects:
 * - `serverUrl` includes scheme (https/http). Token must be a valid JWT;
 *   401s during the WebSocket upgrade close the socket immediately and the
 *   auto-reconnect loop will keep retrying with whatever `getToken()`
 *   returns next.
 *
 * Returns:
 * - A handle exposing connect/disconnect/destroy, RPC functions, and event
 *   hooks. RPC closures resolve the live `EventContext` per invocation so a
 *   reconnect-induced context swap is transparent. In-flight RPCs reject on
 *   disconnect with `chat-ws: rpc cancelled` so callers do not hang
 *   indefinitely (eventa@0.3.0 does not flush its internal pending maps when
 *   the underlying context is disposed; we wrap each invoke in a race).
 */
/**
 * Build the reactive ws URL ref `useWebSocket` watches.
 *
 * `getToken` MUST read from a reactive source (Pinia store ref, Vue ref,
 * computed). A non-reactive read (e.g. `localStorage.getItem`) freezes the
 * URL at first evaluation and `useWebSocket` will reconnect forever with
 * the stale token after the next OIDC refresh — verified by
 * `freezes ws URL when getToken is non-reactive` in ws-client.test.ts.
 */
export function createChatWsUrlRef(
  enabled: Ref<boolean>,
  getToken: () => string | null,
  serverUrl: string,
): ComputedRef<string | undefined> {
  return computed(() => {
    if (!enabled.value)
      return undefined
    const token = getToken()
    if (!token)
      return undefined
    return buildChatWsUrl(serverUrl, token)
  })
}

export function createChatWsClient(options: CreateChatWsClientOptions): ChatWsClient {
  // `enabled` mirrors user intent: connect() flips on, disconnect() flips off.
  // The url ref returns `undefined` when disabled, which makes useWebSocket
  // close cleanly without firing the auto-reconnect loop.
  const enabled = ref(false)
  const urlRef = createChatWsUrlRef(enabled, options.getToken, options.serverUrl)

  // The eventa context is rebuilt on every `onConnected` so RPC + push
  // listeners survive a reconnect by re-binding to the fresh ws.
  // In-flight invokes auto-reject on socket close because the native ws
  // adapter registers wsDisconnectedEvent / wsErrorEvent as abort events on
  // the context (see @moeru/eventa adapters/websocket/native).
  const context = shallowRef<WsEventContext | undefined>(undefined)
  const contextDisposers: Array<() => void> = []
  const newMessagesHandlers = new Set<(payload: NewMessagesPayload) => void>()
  const configHandlers = new Set<(payload: ChatConfigPayload) => void>()
  const statusHandlers = new Set<(status: ChatWsStatus) => void>()

  function notifyStatus(next: ChatWsStatus) {
    for (const handler of statusHandlers) {
      try {
        handler(next)
      }
      catch (err) {
        // Listener errors must not poison the status pipeline.
      }
    }
  }

  function disposeContext() {
    while (contextDisposers.length > 0) {
      const dispose = contextDisposers.pop()!
      try {
        dispose()
      }
      catch {}
    }
    context.value = undefined
  }

  function attachContextListeners(ctx: WsEventContext) {
    contextDisposers.push(ctx.on(newMessages, (event) => {
      // External boundary: validate the wire payload before fanning it out.
      // A malformed server push would otherwise flow unchecked into every
      // subscriber and into `mergeCloudMessagesIntoSession`.
      const result = v.safeParse(NewMessagesPayloadSchema, event.body)
      if (!result.success) {
        return
      }
      const payload = result.output
      for (const handler of newMessagesHandlers) {
        try {
          handler(payload)
        }
        catch (err) {
        }
      }
    }))

    contextDisposers.push(ctx.on(chatConfig, (event) => {
      // The server pushes `chat:config` once right after the handshake. We
      // validate for the same reason as `newMessages`: a drifted server shape
      // must not feed a bad `pushDebounceMs` into the session store.
      const result = v.safeParse(ChatConfigPayloadSchema, event.body)
      if (!result.success) {
        return
      }
      const payload = result.output
      for (const handler of configHandlers) {
        try {
          handler(payload)
        }
        catch (err) {
        }
      }
    }))

    contextDisposers.push(ctx.on(wsErrorEvent, (_event) => {
    }))
  }

  // The url-as-ref form lets useWebSocket reconnect when `urlRef` changes
  // (token rotation, disconnect intent). VueUse internally compares the
  // value and reopens; passing `undefined` cleanly closes any open socket.
  const ws = useWebSocket<string>(urlRef, {
    immediate: false,
    autoClose: true,
    autoReconnect: {
      retries: RECONNECT_RETRIES,
      delay: r => computeReconnectDelay(r, RECONNECT_BASE_MS, RECONNECT_MAX_MS),
    },
    onConnected(rawWs) {
      const created = createWsContext(rawWs)
      context.value = created.context
      attachContextListeners(created.context)
    },
    onDisconnected(_rawWs, ev) {
      disposeContext()
      // ROOT CAUSE:
      //
      // useWebSocket's autoReconnect treats every onclose as worth
      // retrying. When the server rejects auth, the only structured
      // signal we get is the close `code` (the close `reason` body is
      // also delivered but not used for routing here). 4001 is our
      // contract with apps/server/src/app.ts for "this token will never
      // succeed without rotation"; calling `ws.close()` here sets
      // useWebSocket's internal `explicitlyClosed` flag so the next
      // onclose path skips the reconnect schedule. The next time
      // `urlRef` changes (token refresh), `watch(urlRef, open)` calls
      // `open()` which resets `explicitlyClosed` to false and re-inits.
      if (ev.code === WS_CLOSE_UNAUTHORIZED) {
        ws.close()
      }
    },
    onError(_rawWs, _event) {
    },
  })

  // Translate VueUse's 3-state status into our 4-state machine and fan it
  // out to the orchestrator. The chat store creates this inside a Pinia
  // setup, which gives us a parent effect scope for `watch` to attach to.
  // NOTICE:
  // We intentionally do NOT stop this watcher in `disconnect()`; previous
  // behavior killed it permanently and any caller that did `disconnect()`
  // followed by `connect()` silently stopped receiving status events. The
  // watcher is idle while the socket is closed, so leaving it attached
  // costs nothing. Use `destroy()` for terminal cleanup.
  const stopStatusWatch = watch(
    [ws.status, enabled],
    ([rawStatus, isEnabled]) => notifyStatus(mapStatus(rawStatus, isEnabled)),
    { immediate: true },
  )

  function getContext(): WsEventContext {
    if (!context.value)
      throw new Error('chat-ws not connected')
    return context.value
  }

  // We pass a function so each invoke resolves the *current* context. After
  // a reconnect, `context` is rebuilt; a captured reference would point at a
  // disposed context. eventa's native ws adapter installs wsDisconnectedEvent
  // and wsErrorEvent as abortOnEvents, so any invoke whose socket dies before
  // a response arrives rejects with a real Error instead of hanging.
  const invokeSendMessages = defineInvoke(getContext, sendMessages)
  const invokePullMessages = defineInvoke(getContext, pullMessages)
  const invokePullAllMessages = defineInvoke(getContext, pullAllMessages)

  return {
    status: () => mapStatus(ws.status.value, enabled.value),
    connect() {
      if (enabled.value && ws.status.value === 'OPEN')
        return
      enabled.value = true
      // urlRef will recompute and useWebSocket reopens; if it was already
      // closed by a previous disconnect, call open() to nudge it.
      if (ws.status.value === 'CLOSED')
        ws.open()
    },
    disconnect() {
      // Flip intent off first so the autoReconnect loop won't fight us, then
      // ask VueUse to close. urlRef is now `undefined` which makes any
      // future automatic open() a no-op until connect() is called again.
      // Status watcher stays attached so callers can disconnect/connect on
      // the same handle.
      enabled.value = false
      ws.close()
      disposeContext()
    },
    destroy() {
      enabled.value = false
      ws.close()
      disposeContext()
      stopStatusWatch()
    },
    sendMessages: req => invokeSendMessages(req),
    pullMessages: req => invokePullMessages(req),
    pullAllMessages: req => invokePullAllMessages(req),
    onNewMessages(handler) {
      newMessagesHandlers.add(handler)
      return () => {
        newMessagesHandlers.delete(handler)
      }
    },
    onConfig(handler) {
      configHandlers.add(handler)
      return () => {
        configHandlers.delete(handler)
      }
    },
    onStatusChange(handler) {
      statusHandlers.add(handler)
      return () => {
        statusHandlers.delete(handler)
      }
    },
  }
}
